import React, {useCallback, useContext, useRef, useState} from "react"
import ReactMarkdown from "react-markdown"
import {useNavigate, useParams} from "react-router-dom"
import {NewOrEditIssue} from "../../../../components/Issues/NewIssue"
import {BackendContext} from "../../../../context/BackendContext"
import {ProfileContext} from "../../../../context/Profile.context"
import {notifyUpdate} from "../../../../functions/ipfs-orbit"
import {getTimeFromNow} from "../../../../functions/time"
import {useFetchIssueDB} from "../../../../hooks/useFetchIssueDB"
import {IssueComment, IssueMeta} from "../../../../models/IssueDB.model"
import styles from "./index.module.scss"
import remarkGfm from "remark-gfm"
import {v4 as uuidv4} from "uuid"

export const IssueDetails = () => {
  const navigate = useNavigate()
  const {ipfs} = useContext(BackendContext)
  const {profile} = useContext(ProfileContext)
  const {issueId, repoId} = useParams()
  const {issueDB, issueReplCnt, setIssueReplCnt} = useFetchIssueDB(repoId)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const commentTextArea = useRef<HTMLTextAreaElement>(null)

  const onCloseOrReopenIssue = useCallback(async (issue: IssueMeta) => {
    if (!ipfs || !issueDB || !issueId) {
      return
    }
    issue.status = issue.status === "open" ? "closed" : "open"
    await issueDB.put(issueId, issue)
    await notifyUpdate(ipfs, issueDB.address.toString())
    navigate(`../issues`)
  }, [ipfs, issueDB, issueId, navigate])

  const onStartEdit = useCallback(() => {
    setIsEditing(true)
  }, [])

  const onEndEdit = useCallback(() => {
    setIsEditing(false)
  }, [])

  const onSubmitComment = useCallback(async (issueId: string) => {
    if (!issueDB || !profile || !ipfs) {
      return
    }

    const displayName = profile.get("name")
    if (!displayName) {
      return
    }

    const rawComment = commentTextArea.current?.value.trim()
    if (!rawComment) {
      return
    }

    const commentId = uuidv4()
    const comment: IssueComment = {
      id: commentId,
      authorName: displayName,
      authorProfileAddress: profile.address.toString(),
      comment: rawComment,
      createAt: Date.now(),
    }

    const issue = issueDB.get(issueId)
    issue[`comment_${commentId}`] = comment

    await issueDB.put(issueId, issue)
    await notifyUpdate(ipfs, issueDB.address.toString())
    setIssueReplCnt(val => val + 1)

    commentTextArea.current!.value = ""
  }, [ipfs, issueDB, profile, setIssueReplCnt])

  if (!issueDB || !issueId || issueReplCnt === undefined) {
    return null
  }

  const issue = issueDB.get(issueId)
  if (!issue) {
    return null
  }

  const title = issue.title
  const description = issue.description
  const status = issue.status
  const createdAt = issue.createAt
  const authorName = issue.authorName

  // Get keys in the issue object
  const comments = Object
    .keys(issue)
    .filter(key => key.startsWith("comment"))
    .map(key => issue[key as keyof IssueMeta] as IssueComment)
    .sort((a, b) => a.createAt - b.createAt)

  const myDisplayName = profile?.get("name")

  return (
    <div className={styles.Container}>
      <button className={styles.EditBtn}
              onClick={onStartEdit}
      >
        Edit
      </button>

      <button className={styles.CloseBtn}
              onClick={() => onCloseOrReopenIssue(issue)}>
        {status === "open" ? "Close" : "Re-open"} Issue
      </button>

      <div className={styles.Title}>{title}</div>
      <div className={styles.Subtitle}>
        #{issueId.slice(0, 8)} - Created {getTimeFromNow(createdAt, true)} by @{authorName}
      </div>

      <div className={styles.DetailContainer}>
        <div className={styles.DetailHeader}>Details</div>
        <ReactMarkdown className={styles.DetailContent} remarkPlugins={[remarkGfm]}>
          {description}
        </ReactMarkdown>
      </div>

      <div className={styles.CommentContainer}>
        <div className={styles.CommentHeader}>Comment ({comments.length})</div>
        {
          comments.map(c => {
            return (
              <div className={styles.NewCommentContainer} key={c.id}>
                <div className={styles.CommentInfo}>
                  <div className={styles.UserName}>
                    @{c.authorName}
                  </div>
                  <div className={styles.Remark}>
                    {getTimeFromNow(c.createAt, true)}
                  </div>
                </div>
                <ReactMarkdown className={styles.CommentContent} remarkPlugins={[remarkGfm]} children={c.comment}/>
              </div>
            )
          })
        }
        <div className={styles.NewCommentContainer}>
          <div className={styles.CommentInfo}>
            <div className={styles.UserName}>
              @{myDisplayName}
            </div>
            <div className={styles.Remark}>
              new comment
            </div>
          </div>
          <textarea placeholder={"Leave a comment"}
                    className={styles.CommentTextArea}
                    ref={commentTextArea}
          />
          <button className={styles.SubmitBtn}
                  onClick={() => onSubmitComment(issueId)}
          >
            Submit
          </button>
        </div>
      </div>

      <NewOrEditIssue open={isEditing}
                      onClose={onEndEdit}
                      db={issueDB}
                      dbReplCnt={issueReplCnt}
                      setDBReplCnt={setIssueReplCnt}
                      isNew={false}
                      title={title}
                      description={description}
                      issueId={issueId}
      />
    </div>
  )
}
